/** 与 Ai3DViewerView 中任务列表 localStorage 键一致 */
export const XINDI_3D_VIEWER_JOBS_KEY = 'aipd-3d-viewer-jobs';

const SESSION_JOBS_KEY = 'aipd-3d-viewer-jobs-session';
const PREVIEW_FALLBACK_PREFIX = 'aipd-3d-preview-fallback:';
const MAX_PERSIST_STRING = 4000;
const MAX_JOBS_AFTER_TRIM = 40;

/** 写入 localStorage 的精简任务结构（省略引擎长 URL，避免配额溢出） */
export type PersistedCadViewerJob = {
  id: string;
  folderPath: string;
  fileName: string;
  displayName?: string;
  size: number;
  taskId: string | null;
  taskDescription?: string;
  uploadProgress?: number;
  stage: string;
  failureReason?: string;
  convertedAt?: number | null;
  offlineHtmlViewPath?: string;
  offlineHtmlObjectPrefix?: string;
  lightweightObjectKey?: string;
  polledOnce?: boolean;
  failPhase?: number;
  skipOfflinePipelineStep?: boolean;
  uploadStartedAt?: number | null;
  lastUploadPersistAt?: number | null;
  uploadRestored?: boolean;
  convertingSince?: number | null;
  offlinePackaging?: boolean;
  offlinePackagingSince?: number | null;
  ndsOrPackageUrl?: string;
  previewPageUrl?: string;
  jsonUrl?: string;
  engineLightweightZipUrl?: string;
};

export type ViewerPreviewFallback = {
  offlineHtmlViewPath: string;
  displayName?: string;
  fileName: string;
};

export type ViewerJobsSaveResult = {
  ok: boolean;
  local: boolean;
  session: boolean;
};

let inMemoryViewerJobs: PersistedCadViewerJob[] = [];

function truncText(s: string | undefined, max = MAX_PERSIST_STRING): string {
  const t = (s || '').trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max)}…`;
}

export function slimJobForLocalStorage(
  job: PersistedCadViewerJob,
  defaultFolderPath: string,
): PersistedCadViewerJob {
  const folderPath = (job.folderPath || '').trim() || defaultFolderPath;
  return {
    id: job.id,
    folderPath,
    fileName: truncText(job.fileName, 512) || 'model',
    displayName: truncText(job.displayName, 512),
    size: job.size ?? 0,
    taskId: job.taskId ?? null,
    taskDescription: truncText(job.taskDescription, 512),
    uploadProgress: job.uploadProgress ?? 0,
    stage: job.stage,
    failureReason: truncText(job.failureReason),
    convertedAt: job.convertedAt ?? null,
    offlineHtmlViewPath: truncText(job.offlineHtmlViewPath, 1024),
    offlineHtmlObjectPrefix: truncText(job.offlineHtmlObjectPrefix, 1024),
    lightweightObjectKey: truncText(job.lightweightObjectKey, 1024),
    polledOnce: !!job.polledOnce,
    failPhase: job.failPhase ?? 0,
    skipOfflinePipelineStep: !!job.skipOfflinePipelineStep,
    uploadStartedAt: job.uploadStartedAt ?? null,
    lastUploadPersistAt: job.lastUploadPersistAt ?? null,
    uploadRestored: !!job.uploadRestored,
    convertingSince: job.convertingSince ?? null,
    offlinePackaging: !!job.offlinePackaging,
    offlinePackagingSince: job.offlinePackagingSince ?? null,
    ndsOrPackageUrl: '',
    previewPageUrl: '',
    jsonUrl: '',
    engineLightweightZipUrl: '',
  };
}

function jobSortKey(j: PersistedCadViewerJob): number {
  return j.convertedAt ?? j.lastUploadPersistAt ?? 0;
}

function mergeJobLists(
  ...lists: PersistedCadViewerJob[][]
): PersistedCadViewerJob[] {
  const map = new Map<string, PersistedCadViewerJob>();
  for (const list of lists) {
    for (const j of list) {
      if (!j?.id) continue;
      const prev = map.get(j.id);
      if (!prev || jobSortKey(j) >= jobSortKey(prev)) {
        map.set(j.id, j);
      }
    }
  }
  return Array.from(map.values()).sort((a, b) => jobSortKey(b) - jobSortKey(a));
}

export function syncViewerJobsMemoryCache(
  jobs: PersistedCadViewerJob[],
  defaultFolderPath: string,
): void {
  inMemoryViewerJobs = jobs.map((j) => slimJobForLocalStorage(j, defaultFolderPath));
}

function readViewerJobsFromSessionStorage(): PersistedCadViewerJob[] {
  try {
    if (typeof sessionStorage === 'undefined') return [];
    const raw = sessionStorage.getItem(SESSION_JOBS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as PersistedCadViewerJob[];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

export function readViewerJobsFromLocalStorage(): PersistedCadViewerJob[] {
  try {
    if (typeof localStorage === 'undefined') return [];
    const raw = localStorage.getItem(XINDI_3D_VIEWER_JOBS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as PersistedCadViewerJob[];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

/** 合并 localStorage、sessionStorage 与内存缓存（预览返回后仍可恢复列表） */
export function readViewerJobsMerged(defaultFolderPath: string): PersistedCadViewerJob[] {
  const local = readViewerJobsFromLocalStorage();
  const session = readViewerJobsFromSessionStorage();
  const memory = inMemoryViewerJobs;
  return mergeJobLists(local, session, memory).map((j) => slimJobForLocalStorage(j, defaultFolderPath));
}

function trySetLocalStorage(payload: string): boolean {
  try {
    if (typeof localStorage === 'undefined') return false;
    localStorage.setItem(XINDI_3D_VIEWER_JOBS_KEY, payload);
    return true;
  } catch {
    return false;
  }
}

function trySetSessionStorage(payload: string): boolean {
  try {
    if (typeof sessionStorage === 'undefined') return false;
    sessionStorage.setItem(SESSION_JOBS_KEY, payload);
    return true;
  } catch {
    return false;
  }
}

/** @returns 是否至少写入一种存储；local 表示 localStorage 是否成功 */
export function saveViewerJobsToLocalStorage(
  jobs: PersistedCadViewerJob[],
  defaultFolderPath: string,
): ViewerJobsSaveResult {
  const slim = jobs.map((j) => slimJobForLocalStorage(j, defaultFolderPath));
  syncViewerJobsMemoryCache(jobs, defaultFolderPath);

  let payload = '';
  try {
    payload = JSON.stringify(slim);
  } catch {
    return { ok: false, local: false, session: false };
  }

  let local = trySetLocalStorage(payload);
  if (!local) {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(XINDI_3D_VIEWER_JOBS_KEY);
      }
    } catch {
      /* ignore */
    }
    local = trySetLocalStorage(payload);
  }

  if (!local && slim.length > MAX_JOBS_AFTER_TRIM) {
    const trimmed = slim
      .slice()
      .sort((a, b) => jobSortKey(b) - jobSortKey(a))
      .slice(0, MAX_JOBS_AFTER_TRIM);
    try {
      payload = JSON.stringify(trimmed);
    } catch {
      return { ok: trySetSessionStorage(payload), local: false, session: trySetSessionStorage(payload) };
    }
    local = trySetLocalStorage(payload);
    if (!local) {
      try {
        if (typeof localStorage !== 'undefined') localStorage.removeItem(XINDI_3D_VIEWER_JOBS_KEY);
      } catch {
        /* ignore */
      }
      local = trySetLocalStorage(payload);
    }
  }

  const session = trySetSessionStorage(payload);
  return { ok: local || session, local, session };
}

export function setViewerPreviewFallback(jobId: string, payload: ViewerPreviewFallback): void {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(PREVIEW_FALLBACK_PREFIX + jobId, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function getViewerPreviewFallback(jobId: string): ViewerPreviewFallback | null {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    const raw = sessionStorage.getItem(PREVIEW_FALLBACK_PREFIX + jobId);
    if (!raw) return null;
    const p = JSON.parse(raw) as ViewerPreviewFallback;
    const path = p.offlineHtmlViewPath?.trim();
    if (!path) return null;
    return {
      offlineHtmlViewPath: path,
      displayName: p.displayName,
      fileName: p.fileName || '',
    };
  } catch {
    return null;
  }
}

export function readPreviewNavState(): ViewerPreviewFallback | null {
  try {
    const st = history.state as Record<string, unknown> | null;
    const path = typeof st?.offlineHtmlViewPath === 'string' ? st.offlineHtmlViewPath.trim() : '';
    if (!path) return null;
    return {
      offlineHtmlViewPath: path,
      displayName: typeof st?.displayName === 'string' ? st.displayName : '',
      fileName: typeof st?.fileName === 'string' ? st.fileName : '',
    };
  } catch {
    return null;
  }
}

const STORAGE_WARN_KEY = 'aipd-3d-viewer-storage-warned';

/** 启动时迁移旧版「胖」任务数据，避免 localStorage 配额撑满导致无法保存 */
export function migrateAndPruneViewerLocalStorage(defaultFolderPath: string): {
  pruned: boolean;
  bytesBefore: number;
  bytesAfter: number;
} {
  let bytesBefore = 0;
  let bytesAfter = 0;
  let pruned = false;
  try {
    if (typeof localStorage === 'undefined') {
      return { pruned: false, bytesBefore: 0, bytesAfter: 0 };
    }
    const raw = localStorage.getItem(XINDI_3D_VIEWER_JOBS_KEY);
    if (!raw) return { pruned: false, bytesBefore: 0, bytesAfter: 0 };
    bytesBefore = raw.length * 2;
    let arr: PersistedCadViewerJob[];
    try {
      arr = JSON.parse(raw) as PersistedCadViewerJob[];
      if (!Array.isArray(arr)) return { pruned: false, bytesBefore, bytesAfter: bytesBefore };
    } catch {
      localStorage.removeItem(XINDI_3D_VIEWER_JOBS_KEY);
      return { pruned: true, bytesBefore, bytesAfter: 0 };
    }

    const hasFatField = arr.some((j) => {
      const zip = (j.engineLightweightZipUrl || '').length;
      const nds = (j.ndsOrPackageUrl || '').length;
      const prev = (j.previewPageUrl || '').length;
      return zip > 200 || nds > 200 || prev > 200;
    });

    const slim = arr.map((j) => slimJobForLocalStorage(j, defaultFolderPath));
    let payload = JSON.stringify(slim);
    bytesAfter = payload.length * 2;

    if (hasFatField || bytesAfter > 4 * 1024 * 1024) {
      const trimmed = slim
        .slice()
        .sort((a, b) => jobSortKey(b) - jobSortKey(a))
        .slice(0, MAX_JOBS_AFTER_TRIM);
      payload = JSON.stringify(trimmed);
      bytesAfter = payload.length * 2;
      pruned = true;
    }

    if (pruned || hasFatField || bytesAfter < bytesBefore) {
      localStorage.setItem(XINDI_3D_VIEWER_JOBS_KEY, payload);
      pruned = true;
    }
  } catch {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(XINDI_3D_VIEWER_JOBS_KEY);
        pruned = true;
      }
    } catch {
      /* ignore */
    }
  }
  return { pruned, bytesBefore, bytesAfter };
}

/** 是否应在本次会话提示用户清理过存储（仅提示一次） */
export function shouldWarnViewerStoragePruned(): boolean {
  try {
    if (typeof sessionStorage === 'undefined') return false;
    return sessionStorage.getItem(STORAGE_WARN_KEY) !== '1';
  } catch {
    return false;
  }
}

export function markViewerStoragePrunedWarned(): void {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(STORAGE_WARN_KEY, '1');
    }
  } catch {
    /* ignore */
  }
}

export function appendArchivedShareJobToLocalStorage(
  entry: {
    clientJobId: string;
    objectPrefix: string;
    viewPath: string;
    title: string;
  },
  defaultFolderPath = '默认',
): void {
  try {
    const list = readViewerJobsMerged(defaultFolderPath);
    const id = `lw-arch-${entry.clientJobId}`;
    if (list.some((x) => x.id === id)) return;
    const fileLabel = entry.title.trim() || '归档预览';
    list.unshift(
      slimJobForLocalStorage(
        {
          id,
          folderPath: defaultFolderPath,
          fileName: fileLabel,
          displayName: fileLabel,
          size: 0,
          taskId: null,
          taskDescription: 'share-archive',
          uploadProgress: 100,
          stage: 'done',
          failureReason: '',
          convertedAt: Date.now(),
          offlineHtmlViewPath: entry.viewPath,
          offlineHtmlObjectPrefix: entry.objectPrefix,
          previewPageUrl: '',
          polledOnce: true,
          failPhase: 0,
          skipOfflinePipelineStep: true,
        },
        defaultFolderPath,
      ),
    );
    saveViewerJobsToLocalStorage(list, defaultFolderPath);
  } catch {
    /* ignore */
  }
}
